import { faFilePdf, faReceipt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DataGrid, { Column, Pager, Paging, SearchPanel, Summary, TotalItem } from 'devextreme-react/cjs/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import moment from 'moment';
import React, { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getAllStoreLedgerData, getAllStoreReciept } from './services/storeLedger.service';
import PdfViewerModal from 'modules/return/return-to-mayave-request/components/PdfViewerModal';
import Loader from 'Common/Loader';
import notistack from 'modules/core/notistack';
import { HasPermission } from 'Layout/Permissions/permissionsCache';

const StoreLedgerModal = lazy(() =>
    import('./StoreLedgerModal')
        .then(module => ({ default: module.StoreLedgerModal }))
);

interface CustomerData {
    id: number;
    customerId: number;
    customerName: string;
    customerEmail: string;
    customerMobileNo: string;
    customerAddress: string;
    refundableBalance: number;
}

interface SelectedCustomer {
    id: number;
    name: string;
}

const StoreLedger: React.FC = () => {
    const [showStoreLedgerModal, setShowStoreLedgerModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfData, setPdfData] = useState<string | null>(null);
    const [loadingPdf, setLoadingPdf] = useState(false);


    const handleStoreLedger = useCallback((data: CustomerData) => {
        if (!data?.customerId || !data?.customerName) {
            toast.error("Customer details are missing.");
            return;
        }

        setSelectedCustomer({
            id: data.customerId,
            name: data.customerName
        });
        setShowStoreLedgerModal(true);
    }, []);

    const dataSource = useMemo(() => {
        return new CustomStore({
            key: 'id',
            load: async (loadOptions) => {
                try {
                    const search = JSON.parse(JSON.stringify(loadOptions));
                    const parameterList = new URLSearchParams();

                    for (const [key, value] of Object.entries(search)) {
                        parameterList.append(key, JSON.stringify(value));
                    }

                    const response = await getAllStoreLedgerData(parameterList);
                    return response
                } catch (error) {
                    console.error("Data loading error:", error);
                    return { data: [], totalCount: 0 };
                }
            }
        });
    }, []);

    const handleViewPdf = useCallback(async (data: any) => {
        setLoadingPdf(true);

        try {
            const response = await getAllStoreReciept(data);

            if (response.status === 200) {
                setPdfData(response.data);
                setShowPdfModal(true);
            } else {
                notistack.notifyFromApi(response);
            }
        } catch (error) {
            console.error("Error fetching PDF:", error);
            toast.error("Failed to load PDF");
        } finally {
            setLoadingPdf(false);
        }
    }, []);

    const formatCurrency = useCallback((amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    }, []);

    const handleModalClose = useCallback(() => {
        setShowStoreLedgerModal(false);
        setSelectedCustomer(null);
    }, []);

    const handlePdfModalClose = () => {
        setShowPdfModal(false)
        setPdfData(null)
    }

    return (
        <React.Fragment>
            {loadingPdf && <Loader />}
            <div className="page-content">
                <div className="container-fluid">
                    <Card>
                        <Card.Header className='d-flex justify-content-between align-items-center'>
                            <h5 className='m-0'>List Of Store Ledger</h5>
                        </Card.Header>
                        {HasPermission("Ledger.Read") &&
                            <Card.Body className="pt-0">
                                <DataGrid
                                    dataSource={dataSource}
                                    remoteOperations={{ groupPaging: true }}
                                    showRowLines={true}
                                    keyExpr="id"
                                    showBorders={true}
                                    onContentReady={(e) => {
                                        const hasData = e.component.totalCount() > 0;
                                        e.component.option("pager.visible", hasData);
                                        e.component.option("paging.visible", hasData);
                                    }}
                                >
                                    <Pager
                                        allowedPageSizes={[10, 25, 50, 100]}
                                        showPageSizeSelector={true}
                                        showNavigationButtons={true}
                                        displayMode="full"
                                        showInfo={true}
                                    />
                                    <Summary>
                                        <TotalItem
                                        // column="Sr.No."
                                        // summaryType="count"
                                        // displayFormat="Total : {0}"
                                        />
                                    </Summary>
                                    <Paging defaultPageSize={10} />
                                    <SearchPanel visible width={240} placeholder="Search..." />
                                    <Column
                                        allowFiltering={false} dataField="action"
                                        caption="Action"
                                        dataType="string"
                                        width={100}
                                        cellRender={({ data }) => (
                                            <FontAwesomeIcon style={{ cursor: 'pointer' }} title="View Receipt" onClick={() => handleViewPdf(data?.id)} icon={faFilePdf} />
                                            // <FontAwesomeIcon type='button' onClick={() => handleStoreLedger(data)} icon={faReceipt} title="View Customer Ledger" />
                                        )}
                                    />
                                    <Column dataField="narration" caption="Narration" dataType="string" allowFiltering={true} />

                                    <Column dataField="transactionType" width={100} caption="Type" dataType="string" allowFiltering={true} />

                                    <Column
                                        dataField="transactionAmount"
                                        caption="Amount"
                                        dataType="number"
                                        width={150}
                                        allowFiltering={false}
                                        cellRender={({ value }) => <span>{formatCurrency(value)}</span>}
                                    />
                                    <Column dataField="billNo" caption="Bill No" dataType="string" allowFiltering={true} />
                                    <Column
                                        dataField="createdOn"
                                        caption="Date"
                                        dataType="date"
                                        allowFiltering={false}
                                        format="dd/MM/yyyy"
                                        sortOrder='desc'
                                    />
                                </DataGrid>
                            </Card.Body>
                        }
                    </Card>
                </div>
            </div>

            {showStoreLedgerModal && (
                <Suspense fallback={<div>Loading customer ledger...</div>}>
                    <StoreLedgerModal
                        show={showStoreLedgerModal}
                        onHide={handleModalClose}
                        customerId={selectedCustomer?.id}
                        customerName={selectedCustomer?.name}
                    />
                </Suspense>
            )}

            {/* reciept modal */}
            {showPdfModal && (
                <PdfViewerModal
                    visible={showPdfModal}
                    base64Data={pdfData}
                    onClose={handlePdfModalClose}
                    title={`Receiept`}
                />
            )}
        </React.Fragment>
    );
};

export default StoreLedger;
